/**
 * "Swings to confirm" — top of My Shifts. Each swing a roster admin has posted
 * to the employee, with Accept (after the public-holiday declaration) and
 * Decline (with a required reason). Accepted swings then appear in the
 * calendar below; declined ones stay listed until the roster admin re-sends.
 *
 * `SwingSummary` and `DeclineReasonField` are shared with the public confirm
 * page, so the app and the email link describe a swing the same way.
 * Plan: docs/features/roster-swing-acceptance-plan.md
 */
import { useCallback, useEffect, useState } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import Button from '../shared/Button';
import Modal, { ModalBody, ModalFooter } from '../shared/Modal';
import StatusBadge from '../shared/StatusBadge';
import { useNotification } from '../shared/NotificationModal';
import { swingApi, type MySwing, type SwingDay } from './planner/plannerService';
import { SWING_BADGE, swingDayLabel, swingDayCount } from './swingStatus';
import { TEXT } from '../shared/Typography';

/** Dates, length and the day-by-day list of one swing. */
export function SwingSummary({ siteName, start, end, days }: { siteName: string; start: string; end: string; days: SwingDay[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <p className={TEXT.bodyMuted}>{siteName}</p>
      <p className={TEXT.sectionTitle}>
        {swingDayLabel(start)} – {swingDayLabel(end)}
      </p>
      <p className={TEXT.bodyMuted}>{swingDayCount({ start, end })} days</p>
      {days.length > 0 && (
        <>
          <Button variant="link" size="sm" aria-expanded={open} onClick={() => setOpen(o => !o)}>
            {open ? 'Hide day-by-day' : 'Show day-by-day'}
          </Button>
          {open && (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
              {days.map(d => (
                <li key={d.date} className="flex justify-between gap-3 px-3 py-2">
                  <span className="text-gray-900">{swingDayLabel(d.date, false)}</span>
                  <span className="text-right text-gray-600">
                    {d.label}
                    {d.camp ? ` · ${d.camp}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/** The required reason for declining. */
export function DeclineReasonField({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className={`block ${TEXT.bodyStrong}`}>
        Why can&apos;t you work this swing?
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={1000}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600"
        placeholder="e.g. Booked leave, medical appointment, family commitment"
      />
      <p className={TEXT.bodyMuted}>Your roster admin will see this and follow up with you.</p>
    </div>
  );
}

export default function SwingConfirmCard({ onAnswered }: { onAnswered: () => void }) {
  const notify = useNotification();
  const [swings, setSwings] = useState<MySwing[]>([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [answering, setAnswering] = useState<{ swing: MySwing; action: 'accept' | 'decline' } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await swingApi.mine();
      setSwings(data.swings);
      setDisclaimer(data.disclaimer);
    } catch {
      // Non-fatal: the rest of My Shifts still works; the bell also lists these.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    if (!answering) return;
    setBusy(true);
    try {
      await swingApi.respond(answering.swing._id, answering.action, answering.action === 'decline' ? reason.trim() : undefined);
      notify.showSuccess(
        answering.action === 'accept'
          ? 'Swing accepted. It is now in your roster below.'
          : 'Swing declined. Your roster admin has been told and will be in touch.'
      );
      setAnswering(null);
      setReason('');
      await load();
      onAnswered();
    } catch (e) {
      notify.showError(e instanceof Error ? e.message : 'Could not save your answer. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!swings.length) return null;
  const waiting = swings.filter(s => s.status === 'posted').length;

  return (
    <section aria-labelledby="swings-to-confirm" className="rounded-lg border border-amber-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 sm:px-6">
        <CalendarDaysIcon className="h-6 w-6 shrink-0 text-amber-600" aria-hidden />
        <div>
          <h2 id="swings-to-confirm" className={TEXT.cardTitle}>
            {waiting ? `${waiting} swing${waiting === 1 ? '' : 's'} to confirm` : 'Your swing responses'}
          </h2>
          <p className={TEXT.bodyMuted}>Accepted swings appear in your roster below.</p>
        </div>
      </div>
      <ul className="divide-y divide-gray-200">
        {swings.map(s => (
          <li key={s._id} className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-6">
            <div className="min-w-0 flex-1">
              <SwingSummary siteName={s.siteName} start={s.start} end={s.end} days={s.days} />
              {s.status === 'declined' && (
                <p className={`mt-2 ${TEXT.bodyMuted}`}>
                  You declined this swing{s.declineReason ? `: “${s.declineReason}”` : ''}. Your roster admin will be in touch.
                </p>
              )}
            </div>
            {s.status === 'posted' ? (
              <div className="flex shrink-0 gap-2">
                <Button size="lg" variant="primary" onClick={() => setAnswering({ swing: s, action: 'accept' })}>
                  Accept
                </Button>
                <Button size="lg" variant="outline" onClick={() => { setReason(''); setAnswering({ swing: s, action: 'decline' }); }}>
                  Decline
                </Button>
              </div>
            ) : (
              <StatusBadge status={SWING_BADGE[s.status]} />
            )}
          </li>
        ))}
      </ul>

      <Modal isOpen={!!answering} onClose={() => setAnswering(null)} title={answering?.action === 'accept' ? 'Accept this swing?' : 'Decline this swing'} size="md">
        {answering && (
          <>
            <ModalBody>
              <div className="space-y-4">
                <p className={TEXT.cardTitle}>
                  {answering.swing.siteName} · {swingDayLabel(answering.swing.start, false)} – {swingDayLabel(answering.swing.end)}
                </p>
                {answering.action === 'accept' ? (
                  <p className={`rounded-lg bg-gray-50 p-3 ${TEXT.body}`}>{disclaimer}</p>
                ) : (
                  <DeclineReasonField id="swing-decline-reason" value={reason} onChange={setReason} />
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setAnswering(null)}>Cancel</Button>
              <Button
                variant={answering.action === 'accept' ? 'primary' : 'danger'}
                disabled={busy || (answering.action === 'decline' && !reason.trim())}
                onClick={() => void submit()}
              >
                {answering.action === 'accept' ? 'I accept this swing' : 'Decline swing'}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </section>
  );
}
